import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box, Paper, Button, Alert, Snackbar, Divider, Typography, Chip, Tooltip, CircularProgress,
  Accordion, AccordionSummary, AccordionDetails, IconButton, Switch, TextField,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import AssignmentIcon from '@mui/icons-material/Assignment';
import SaveIcon from '@mui/icons-material/Save';
import ViewStreamIcon from '@mui/icons-material/ViewStream';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import DescriptionIcon from '@mui/icons-material/Description';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import CodeIcon from '@mui/icons-material/Code';
import ArticleIcon from '@mui/icons-material/Article';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import HeadphonesIcon from '@mui/icons-material/Headphones';
import TimelineIcon from '@mui/icons-material/Timeline';
import AudioRecorder from '../components/AudioRecorder';
import WorkflowStepper from '../components/WorkflowStepper';
import PageHeaderNavigation from '../components/common/PageHeaderNavigation';
import AudioPanel from '../components/AudioPanel';
// DiagnosisPanel removed — CPT exam selection replaces diagnosis classification
import TranscriptPanel from '../components/TranscriptPanel';
import PatientDemographicsPanel from '../components/PatientDemographicsPanel';
import CurrentExaminationPanel from '../components/CurrentExaminationPanel';
import ExamPatientValidationAlert from '../components/ExamPatientValidationAlert';
import CPTChipCombo from '../components/CPTChipCombo';
import PatientSearchPanel from '../components/PatientSearchPanel';
import PatientHistorySummary from '../components/PatientHistorySummary';
import StatusPanel from '../components/StatusPanel';
import RichReportEditor from '../components/RichReportEditor';
import type { PatientContextInput, PatientResponse } from '../services/patientService';
import { useAppSelector, useAppDispatch } from '../hooks/reduxHooks';
import {
  saveTranscription,
  resetTranscription,
  clearError,
  loadSessionForAppend,
  enterContinueMode,
  commitEditedCorrectedText,
  setDeferredCorrectionEnabled,
  addCorrectedTranscript,
} from '../store/transcriptionSlice';
import systemConfigService from '../services/systemConfigService';
import { generateReport, regenerateReport, clearReport, setReport, updateReport, finalizeReport, selectIsReportEditable } from '../store/reportSlice';
import {
  selectPipelineReadiness,
  selectGenerateButtonState,
  selectDictationJustCompleted,
} from '../store/workflowSelectors';
import { selectShowTranscriptionToolbarToggles, selectShowContextPanels, toggleContextPanels } from '../store/uiPreferencesSlice';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import transcriptionService from '../services/transcriptionService';
import patientService from '../services/patientService';
import reportService from '../services/reportService';
import { exportReportPDF, printReport, downloadReportText, type PatientContext } from '../utils/reportExport';
import { copyReportToClipboard } from '../utils/reportUtils';
import { clearValidation } from '../store/validationSlice';
import { fetchCPTExam, detectCPTFromTranscript, resetCPTState, setManuallySet, selectExam, clearDetection } from '../store/cptExamSlice';
import type { CPTExam } from '../services/cptExamService';
import { resolveLaterality } from '../services/cptExamService';
import {
  fetchUserPreferences,
  selectUseCustomTemplateByDefault,
} from '../store/userPreferencesSlice';

const TranscriptionPage: React.FC = () => {
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [sessionHasAudio, setSessionHasAudio] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [isContinueMode, setIsContinueMode] = useState(false);
  const [autoStartRecording, setAutoStartRecording] = useState(false);
  const [stopRecordingTrigger, setStopRecordingTrigger] = useState(0);
  const [isStopping, setIsStopping] = useState(false);
  const [showDictationCompleteSnack, setShowDictationCompleteSnack] = useState(false);
  const [patientContext, setPatientContext] = useState<PatientContextInput>({});
  const [selectedPatient, setSelectedPatient] = useState<PatientResponse | null>(null);
  const [patientAccordionOpen, setPatientAccordionOpen] = useState(false);
  const [examAccordionOpen, setExamAccordionOpen] = useState(false);
  const [reportLayout, setReportLayout] = useState<'bottom' | 'right'>('right');

  // Custom template state
  const [useCustomTemplate, setUseCustomTemplate] = useState(false);
  const [customTemplateText, setCustomTemplateText] = useState('');
  const useCustomTemplateByDefault = useAppSelector(selectUseCustomTemplateByDefault);

  // Gap 1 & 2: Toggle visibility - dictation capture always hidden by default on load (not persisted)
  const [showDictationCapture, setShowDictationCapture] = useState(false);
  const [showAudioPlayback, setShowAudioPlayback] = useState(false);
  
  // Item 2: Toggle visibility for patient/exam context panels (persisted to localStorage via Redux)
  const showContextPanels = useAppSelector(selectShowContextPanels);

  // WorkflowStepper toggle — off by default for new and existing sessions
  const [showWorkflowStepper, setShowWorkflowStepper] = useState(false);
  
  // Item 4: CPT chip combo — inline editable, no separate popover state needed
  const {
    text, correctedText, isFinal, sessionId, loading, error,
    isAppendMode, isEditingCorrected, isIncrementalCorrecting, isRefiningInFlight,
    editedCorrectedText, isManualTypingMode, manualTypedText,
  } = useAppSelector((state) => state.transcription);
  const { report, loading: reportLoading, isFinalized, isSaving: reportSaving, config: reportConfig } = useAppSelector((state) => state.report);
  const hardLockEnabled = reportConfig?.hardLockEnabled ?? false;
  const [editedReportText, setEditedReportText] = useState<string | null>(null);
  const [reportSaveSuccess, setReportSaveSuccess] = useState(false);
  const [showFinalizeConfirm, setShowFinalizeConfirm] = useState(false);
  const [lastSavedText, setLastSavedText] = useState<string | null>(null);
  const [lastSavedPatientContext, setLastSavedPatientContext] = useState<string>('{}');
  const [lastSavedCPTCode, setLastSavedCPTCode] = useState<string | null>(null);
  const [reportVersion, setReportVersion] = useState(0);
  const [savedReportVersion, setSavedReportVersion] = useState(0);
  const [examSavedAt, setExamSavedAt] = useState<number | undefined>(undefined);
  const [copied, setCopied] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [autoSaveError, setAutoSaveError] = useState<string | null>(null);
  const [exportPatientContext, setExportPatientContext] = useState<PatientContext | undefined>(undefined);
  const { selectedExam: selectedCPTExam, manuallySet: cptManuallySet, detecting: cptDetecting, detection: cptDetection } = useAppSelector((state) => state.cptExams);
  const {
    blocked: validationBlocked,
    issues: validationIssues,
    loading: validationLoading,
  } = useAppSelector((state) => state.validation);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // location.key === 'default' on initial app load — indicates user deep-linked here.
  // Anything else means there is at least one in-app navigation entry to go back to.
  // Defensive: location.key may be undefined in some router configurations.
  const canGoBackInApp = Boolean(location.key && location.key !== 'default');

  // UI preference: show/hide toolbar toggle buttons
  const showToolbarToggles = useAppSelector(selectShowTranscriptionToolbarToggles);

  // Workflow selectors
  const readiness = useAppSelector(selectPipelineReadiness);
  const generateBtnState = useAppSelector(selectGenerateButtonState);
  const dictationJustCompleted = useAppSelector(selectDictationJustCompleted);

  // Track previous transcribing state for auto-prompt detection
  const wasTranscribingRef = useRef(false);
  
  // Check if we're in view-only mode (for finalized reports)
  // Hard lock disabled → allow editing even when finalized
  const viewMode = searchParams.get('mode') === 'view';
  const isReportEditable = useAppSelector(selectIsReportEditable);
  const isReadOnly = viewMode || (!isReportEditable && isFinalized);

  const deferredCorrectionEnabled = useAppSelector((state) => state.transcription.deferredCorrectionEnabled);

  // Load user preferences and deferred correction config on mount
  useEffect(() => {
    dispatch(fetchUserPreferences());
    systemConfigService.getAllConfig()
      .then((configs) => {
        const deferred = configs.find((c) => c.key === 'ENABLE_DEFERRED_CORRECTION');
        if (deferred) {
          const raw = String(deferred.value ?? 'false').toLowerCase();
          const enabled = raw === 'enabled' || raw === 'true' || raw === '1' || raw === 'yes';
          dispatch(setDeferredCorrectionEnabled(enabled));
        }
      })
      .catch((err: unknown) => {
        console.warn('Failed to load deferred correction config:', err);
      });
  }, [dispatch]);

  // Sync local toggle state with user preferences only once on initial load.
  // A ref guards against background Redux updates overwriting manual user changes.
  const hasInitializedToggle = useRef(false);
  useEffect(() => {
    if (!hasInitializedToggle.current) {
      setUseCustomTemplate(useCustomTemplateByDefault);
      hasInitializedToggle.current = true;
    }
  }, [useCustomTemplateByDefault]);

  // Load existing session if ?session=<id> is in the URL
  useEffect(() => {
    const resumeSessionId = searchParams.get('session');
    if (resumeSessionId) {
      dispatch(clearReport());
      // Preemptively block CPTExamSelector's auto-detect-from-transcript effect
      // during the session-load window. Without this, the child's effect races
      // the async getSessionDetail call and fires a redundant /cpt/detect that
      // can overwrite the saved exam. If the session turns out to have NO saved
      // CPT, we clear this flag below before running auto-detection.
      dispatch(setManuallySet(true));

      let isActive = true;

      // Load existing session for viewing/editing/continuing
      (async () => {
        try {
          const detail = await transcriptionService.getSessionDetail(resumeSessionId);
          if (!isActive) return;

          dispatch(loadSessionForAppend({
            sessionId: detail.session_id,
            rawText: detail.text,
            correctedText: detail.corrected_text || '',
            isUserEdited: detail.is_user_edited || false,
            classification: {
              category: detail.diagnosis_category || null,
              pathologyType: detail.pathology_type || null,
              confidence: detail.classification_confidence || null,
            },
          }));
          // Mark session as already saved so Save button starts disabled
          const loadedText = detail.corrected_text || detail.text || '';
          setLastSavedText(loadedText);
          // Initialise CPT snapshot from session or report-level CPT data
          const savedCptCode = detail.cpt_code || detail.report?.cpt_code || null;
          setLastSavedCPTCode(savedCptCode);

          // Session detail is authoritative for the selected session's report.
          dispatch(setReport(detail.report));

          // Auto-populate CPT exam selector if CPT data exists from previous report.
          // Check both top-level session detail fields AND report-level fields as fallback.
          let loadedCPTExam: CPTExam | null = null;
          const effectiveCptCode = detail.cpt_code || detail.report?.cpt_code || null;
          const effectiveCptExamName = detail.cpt_exam_name || detail.report?.cpt_exam_name || null;
          const hasSavedCPT = Boolean(effectiveCptCode);
          if (hasSavedCPT) {
            // Determine whether the saved CPT was manually selected or auto-detected.
            // Only lock out re-detection (manuallySet=true) when the user explicitly
            // chose the CPT. Auto-detected CPTs should be allowed to re-detect after
            // transcript edits.
            const savedDetectionMethod = detail.cpt_detection_method || detail.report?.cpt_detection_method;
            const wasManuallySelected = savedDetectionMethod === 'manual';
            dispatch(setManuallySet(wasManuallySelected));
            console.log('CPT_LOAD | session_id=%s | detection_method=%s | manuallySet=%s', detail.session_id, savedDetectionMethod, wasManuallySelected);

            // Immediately set a minimal CPTExam from session detail so the header
            // chip renders right away, before the async fetchCPTExam resolves.
            const savedLaterality = detail.cpt_laterality || detail.report?.cpt_laterality || null;
            const savedContrast = detail.cpt_contrast || detail.report?.cpt_contrast || null;
            const fallbackExam = {
              id: 0,
              cpt_code: effectiveCptCode || '',
              exam_name: effectiveCptExamName || effectiveCptCode || '',
              modality: '',
              body_region: '',
              subspecialty: null,
              laterality: savedLaterality,
              contrast: savedContrast,
              description: null,
              report_template: null,
              is_active: true,
            };
            dispatch(selectExam(fallbackExam));

            try {
              loadedCPTExam = await dispatch(fetchCPTExam({
                cptCode: effectiveCptCode ?? '',
                examName: effectiveCptExamName ?? undefined
              })).unwrap();
              // Merge saved laterality/contrast into DB exam if the DB record
              // lacks them (DB records are often generic; per-session specifics
              // come from classification and must be restored on reload).
              const mergedExam: CPTExam = {
                ...loadedCPTExam,
                laterality: (!loadedCPTExam.laterality || loadedCPTExam.laterality === 'none') && savedLaterality
                  ? savedLaterality
                  : loadedCPTExam.laterality,
                contrast: (!loadedCPTExam.contrast || loadedCPTExam.contrast === 'none') && savedContrast
                  ? savedContrast
                  : loadedCPTExam.contrast,
              };
              dispatch(selectExam(mergedExam));
              console.log(
                'CPT_AUTO_POPULATED | session_id=%s | cpt_code=%s | method=%s | laterality=%s | contrast=%s',
                detail.session_id, detail.cpt_code, detail.cpt_detection_method, mergedExam.laterality, mergedExam.contrast,
              );
            } catch (cptErr) {
              // Fallback already set above — log and continue
              console.warn('Failed to load full CPT exam from DB, using session data:', cptErr);
            }
          }
          if (!hasSavedCPT) {
            // Check if the user explicitly cleared CPT for this session — skip auto-detect if so.
            const cptClearedKey = `cpt_cleared_${resumeSessionId}`;
            const userClearedCPT = sessionStorage.getItem(cptClearedKey) === 'true';
            const transcript = detail.corrected_text || detail.text;
            if (userClearedCPT) {
              // User explicitly cleared CPT for this session — keep it cleared, allow manual select
              dispatch(setManuallySet(true));
              console.log('CPT_SKIPPED_USER_CLEARED | session_id=%s', resumeSessionId);
            } else if (isActive && transcript && transcript.length > 50) {
              try {
                // Set manuallySet FIRST to block CPTExamSelector's auto-detect effect
                // from racing us. The selector's effect checks !manuallySet before running.
                dispatch(setManuallySet(true));
                const detected = await dispatch(detectCPTFromTranscript({ transcript, useLlm: true, sessionId: resumeSessionId })).unwrap();
                if (detected?.cpt_code) {
                  loadedCPTExam = {
                    id: 0,
                    cpt_code: detected.cpt_code,
                    exam_name: detected.exam_name,
                    modality: detected.modality,
                    body_region: detected.body_region,
                    subspecialty: null,
                    laterality: detected.laterality ?? null,
                    contrast: detected.contrast ?? null,
                    description: null,
                    report_template: null,
                    is_active: true,
                  };
                  // Set the detected exam so the header chip renders
                  dispatch(selectExam(loadedCPTExam));
                  setLastSavedCPTCode(detected.cpt_code);
                  // Clear the detection so CPTExamSelector doesn't try to auto-select again
                  dispatch(clearDetection());
                }
                console.log(
                  'CPT_AUTO_DETECTED | session_id=%s | cpt_code=%s | method=llm_from_transcript',
                  detail.session_id, detected?.cpt_code,
                );
              } catch (detectErr) {
                console.warn('CPT auto-detection from transcript failed:', detectErr);
                // Re-enable auto-detection on failure so user can manually select
                dispatch(setManuallySet(false));
              }
            } else {
              // No transcript or too short - allow manual selection
              dispatch(setManuallySet(false));
            }
          }

          // Mark session as having audio if it was created via WebSocket (has a session_id and duration)
          if (detail.duration_seconds && detail.duration_seconds > 0) {
            setSessionHasAudio(true);
          }

          // Load patient context if available (includes study details)
          // Merge CPT exam data as fallback for empty fields
          let patientData: PatientResponse | null = null;
          try {
            patientData = await patientService.getSessionPatient(resumeSessionId);
          } catch (patientErr: unknown) {
            // Patient not found is OK - just means no patient context was provided
            const status = (patientErr as { response?: { status?: number } })?.response?.status;
            if (status !== 404) {
              console.warn('Failed to load patient context:', patientErr);
            }
          }

          if (isActive) {
            const cptLaterality = loadedCPTExam
              ? (resolveLaterality(loadedCPTExam) || '')
              : '';
            if (patientData) {
              setSelectedPatient(patientData);
            }
            // Always build patientContext so CPT-detected study fields populate even
            // when no patient is linked to the session.
            const loadedPatientCtx = {
              first_name: patientData?.first_name || '',
              last_name: patientData?.last_name || '',
              mrn: patientData?.mrn || '',
              dob: patientData?.dob || '',
              gender: patientData?.gender || '',
              accession_number: patientData?.accession_number || '',
              modality: patientData?.modality || loadedCPTExam?.modality || '',
              study_date: patientData?.study_date || '',
              body_part: patientData?.body_part || loadedCPTExam?.body_region || '',
              study_description: patientData?.study_description || loadedCPTExam?.exam_name || '',
              clinical_indication: patientData?.clinical_indication || '',
              study_priority: patientData?.study_priority || '',
              laterality: patientData?.laterality || cptLaterality,
              ordering_physician_name: patientData?.ordering_physician_name || '',
            };
            setPatientContext(loadedPatientCtx);
            setLastSavedPatientContext(JSON.stringify(loadedPatientCtx));
            // Signal the exam panel that the loaded state is its saved baseline.
            // Needed because Redux selectedExam may resolve before setPatientContext,
            // so the panel's own cpt_code-triggered baseline capture sees an empty value.
            setExamSavedAt(Date.now());
          }
        } catch (err: unknown) {
          if (!isActive) return;

          const axiosErr = err as { response?: { status?: number; data?: { detail?: string } } };
          if (axiosErr.response?.status === 403) {
            console.error('Access denied for session:', resumeSessionId);
            navigate('/transcriptions');
          } else {
            console.error('Failed to load session for resume:', err);
          }
        }
      })();

      return () => {
        isActive = false;
      };
    } else {
      // New session - reset everything including patient context, CPT, and validation
      dispatch(resetTranscription());
      dispatch(clearReport());
      dispatch(resetCPTState());
      dispatch(clearValidation());
      setPatientContext({});
      setSelectedPatient(null);
      setLastSavedText(null);
      setSessionHasAudio(false);
      setPatientAccordionOpen(false);
      setExamAccordionOpen(false);
    }
  }, [dispatch, searchParams, navigate]);

  // Auto-prompt snackbar when dictation ends
  useEffect(() => {
    if (wasTranscribingRef.current && !isTranscribing && dictationJustCompleted) {
      console.log('WORKFLOW_DICTATION_COMPLETE | session_id=%s', sessionId);
      setShowDictationCompleteSnack(true);
    }
    wasTranscribingRef.current = isTranscribing;
  }, [isTranscribing, dictationJustCompleted, sessionId]);

  // CPT auto-detect from reviewed text (runs regardless of whether context panels are visible)
  useEffect(() => {
    const transcript = correctedText || text;
    const guards = {
      hasTranscript: !!transcript,
      longEnough: transcript ? transcript.length > 20 : false,
      noSelectedExam: !selectedCPTExam,
      notManuallySet: !cptManuallySet,
      notDetecting: !cptDetecting,
      noDetection: !cptDetection,
      notTranscribing: !isTranscribing,
    };
    const allPass = Object.values(guards).every(Boolean);
    if (allPass) {
      console.log('CPT_AUTO_DETECT_PAGE | transcript_len=%d | session_id=%s', transcript.length, sessionId);
      dispatch(detectCPTFromTranscript({ transcript, useLlm: true, sessionId: sessionId ?? undefined }));
    } else {
      const failed = Object.entries(guards).filter(([, v]) => !v).map(([k]) => k);
      console.log('CPT_AUTO_DETECT_BLOCKED | failed=%s | transcript_len=%d', failed.join(','), transcript?.length ?? 0);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [correctedText, text, selectedCPTExam, cptManuallySet, cptDetecting, cptDetection, isTranscribing, dispatch]);

  // Handle completed CPT detection at page level so the UI chip shows even when
  // the CurrentExaminationPanel accordion is collapsed or showContextPanels is false.
  useEffect(() => {
    if (
      cptDetection &&
      !selectedCPTExam &&
      !cptManuallySet &&
      cptDetection.cpt_code &&
      cptDetection.confidence > 0
    ) {
      console.log(
        'CPT_AUTO_SELECT_PAGE | cpt_code=%s | exam=%s | confidence=%.2f',
        cptDetection.cpt_code,
        cptDetection.exam_name,
        cptDetection.confidence,
      );
      // NOTE: do NOT set manuallySet=true here. Auto-detected CPT should be
      // cleared on transcript edit so it can be re-detected. manuallySet is
      // reserved for user-initiated selections (CPTExamSelector, CPTChipCombo).
      console.log('CPT_FETCH_DISPATCH | cpt=%s | exam_name=%s | body_region=%s', cptDetection.cpt_code, cptDetection.exam_name || '(none)', cptDetection.body_region || '(none)');
      dispatch(fetchCPTExam({ cptCode: cptDetection.cpt_code, examName: cptDetection.exam_name || undefined, bodyRegion: cptDetection.body_region }))
        .unwrap()
        .then((exam) => {
          console.log('CPT_FETCH_SUCCESS | cpt=%s | returned_exam=%s | contrast=%s', exam.cpt_code, exam.exam_name, exam.contrast || '(none)');
          // Merge classified laterality/contrast into the DB exam record
          // (DB records are generic; classification adds per-transcript specifics)
          const merged: CPTExam = {
            ...exam,
            laterality: cptDetection.laterality ?? resolveLaterality(exam) ?? exam.laterality,
            contrast: cptDetection.contrast ?? exam.contrast,
          };
          console.log('CPT_FETCH_MERGED | cpt=%s | exam=%s | contrast=%s', merged.cpt_code, merged.exam_name, merged.contrast || '(none)');
          dispatch(selectExam(merged));
        })
        .catch((err) => {
          console.log('CPT_FETCH_FAILED | cpt=%s | error=%s | fallback_exam=%s', cptDetection.cpt_code, err, cptDetection.exam_name);
          // Fallback: build a minimal exam from detection data so the chip renders
          dispatch(selectExam({
            id: 0,
            cpt_code: cptDetection.cpt_code,
            exam_name: cptDetection.exam_name || cptDetection.cpt_code,
            modality: cptDetection.modality || '',
            body_region: cptDetection.body_region || '',
            subspecialty: null,
            laterality: cptDetection.laterality || null,
            contrast: cptDetection.contrast || null,
            description: null,
            report_template: null,
            is_active: true,
          }));
        });
    }
  }, [cptDetection, selectedCPTExam, cptManuallySet, dispatch]);

  const handleTranscriptionStart = () => {
    if (validationBlocked) {
      console.warn(
        'DICTATION_BLOCKED_VALIDATION | session_id=%s | critical_issues=%d',
        sessionId, validationIssues.filter((i) => i.severity === 'critical').length,
      );
      return;
    }
    setIsTranscribing(true);
    setSessionHasAudio(true);
    setAutoStartRecording(false);
    // Use functional update to prevent race if called again before re-render.
    // The dispatch only runs when transitioning from true to false.
    setIsContinueMode((prev) => {
      if (prev || isAppendMode) {
        // Atomically clear isAppendMode + isFinal so the (isFinal && isAppendMode)
        // guard in addCorrectedTranscript cannot trip between two dispatches and
        // drop the first live correction of the new segment.
        // Also fires when isAppendMode=true (session loaded from history) even if
        // isContinueMode was never set — ensures finalizedCorrectedText is cleared
        // so addCorrectedTranscript doesn't prepend stale prior text to the LLM's
        // already-complete combined output.
        dispatch(enterContinueMode());
      }
      return false;
    });
  };
  const handleTranscriptionEnd = () => {
    setIsTranscribing(false);
    setIsStopping(false);
    setAutoStartRecording(false);

    // Fallback fetch: if the final LLM-corrected text was never received over the
    // WebSocket (e.g. the WS closed before the backend's final-pass send completed,
    // which can happen on auto-stop in deferred-correction mode), poll the backend
    // once after a short delay to pick up whatever was persisted in the DB.
    const snapSessionId = sessionId;
    const snapCorrected = correctedText;
    const snapRaw = text;
    if (!snapSessionId) return;

    setTimeout(async () => {
      try {
        const detail = await transcriptionService.getSessionDetail(snapSessionId);
        const persisted = detail.corrected_text;
        // Only apply if the backend has a better corrected text than what we have in Redux.
        // "Better" means: non-empty, different from the raw STT text, and the frontend
        // currently has no corrected text (or still shows raw text as corrected).
        const frontendHasRefinedText =
          snapCorrected && snapCorrected.trim() !== '' && snapCorrected.trim() !== snapRaw?.trim();
        if (persisted && persisted.trim() !== '' && !frontendHasRefinedText) {
          dispatch(addCorrectedTranscript({
            raw: detail.text || snapRaw || '',
            corrected: persisted,
            isIncremental: false,
            isFinal: true,
          }));
        }
      } catch {
        // Best-effort — silently ignore if session fetch fails
      }
    }, 1500);
  };
  const handleTranscriptionStopping = () => {
    // Immediate feedback when stop is initiated - disable the stop button
    setIsStopping(true);
    console.log('[UI] Recording stop initiated, waiting for finalization...');
  };

  const handleSaveExamContext = useCallback(async (ctx: PatientContextInput, overrideSessionId?: string | null) => {
    const sid = overrideSessionId ?? sessionId;
    if (!sid) return;
    try {
      await patientService.updateSessionPatient(sid, {
        ...ctx,
        mrn: selectedPatient?.mrn || ctx.mrn || '',
        dob: selectedPatient?.dob || ctx.dob || '',
        first_name: selectedPatient?.first_name || ctx.first_name,
        last_name: selectedPatient?.last_name || ctx.last_name,
        gender: selectedPatient?.gender || ctx.gender,
      });
    } catch (err) {
      console.error('Failed to save exam context:', err);
      throw err;
    }
  }, [sessionId, selectedPatient]);

  const handleSaveTranscription = useCallback(async () => {
    const textToSave = correctedText || text;
    // For manual-typing path: generate a session UUID now so the backend
    // persists it and report generation can reference it.
    const effectiveSessionId = sessionId ?? (textToSave ? crypto.randomUUID() : null);

    if (textToSave && effectiveSessionId) {
      await dispatch(saveTranscription({
        text,
        sessionId: effectiveSessionId,
        cptCode: selectedCPTExam?.cpt_code,
        cptExamName: selectedCPTExam?.exam_name,
      }));
    }

    // Persist exam/patient context independently of whether transcript changed.
    // Pass effectiveSessionId explicitly to avoid stale-closure on sessionId
    // (saveTranscription.fulfilled updates Redux sessionId but the current
    // handleSaveExamContext closure may still have the old null value).
    if (effectiveSessionId && Object.keys(patientContext).length > 0) {
      try { await handleSaveExamContext(patientContext, effectiveSessionId); } catch { /* best-effort */ }
    }

    setExamSavedAt(Date.now());
    if (textToSave) setLastSavedText(textToSave);
    setLastSavedPatientContext(JSON.stringify(patientContext));
    setLastSavedCPTCode(selectedCPTExam?.cpt_code ?? null);
    setSavedReportVersion(reportVersion);
    setShowSuccessAlert(true);
  }, [correctedText, text, sessionId, selectedCPTExam, dispatch, patientContext, reportVersion, handleSaveExamContext]);

  const handleOpenGenerateDialog = async () => {
    if (validationBlocked) {
      console.warn('REPORT_BLOCKED_VALIDATION | session_id=%s', sessionId);
      return;
    }
    if (!readiness.pipelineReady) {
      console.log('GENERATE_BLOCKED | session_id=%s reasons=%s', sessionId, readiness.blockReasons.join('; '));
      return;
    }

    // Auto-save transcription before generating so the dictation is always persisted.
    // For the manual-typing path this also obtains a sessionId.
    let effectiveSessionId = sessionId;
    const textToSave = correctedText || text;
    if (!effectiveSessionId) {
      if (!textToSave) return;
      effectiveSessionId = crypto.randomUUID();
    }
    if (textToSave) {
      try {
        const saved = await dispatch(saveTranscription({
          text,
          sessionId: effectiveSessionId,
          cptCode: selectedCPTExam?.cpt_code,
          cptExamName: selectedCPTExam?.exam_name,
        })).unwrap();
        effectiveSessionId = saved.sessionId ?? effectiveSessionId;
        setLastSavedText(textToSave);
        setLastSavedPatientContext(JSON.stringify(patientContext));
        setLastSavedCPTCode(selectedCPTExam?.cpt_code ?? null);
        setSavedReportVersion(reportVersion);
      } catch (saveErr) {
        console.error('GENERATE_SAVE_FAILED | session_id=%s error=%s', effectiveSessionId, saveErr);
      }
    }

    if (!effectiveSessionId) {
      console.error('GENERATE_FAILED | could not obtain sessionId');
      return;
    }

    console.log('PIPELINE_READY | session_id=%s', effectiveSessionId);
    // Persist exam context before generating so CPT panel clears dirty state
    if (Object.keys(patientContext).length > 0) {
      try { await handleSaveExamContext(patientContext, effectiveSessionId); } catch { /* best-effort */ }
    }
    setExamSavedAt(Date.now());
    setReportVersion((v) => v + 1);
    const hasPatientCtx = patientContext.mrn && patientContext.dob;
    const result = await dispatch(generateReport({
      sessionId: effectiveSessionId,
      patientContext: hasPatientCtx ? patientContext : undefined,
      cptCode: selectedCPTExam?.cpt_code,
      cptExamName: selectedCPTExam?.exam_name,
      cptDetectionMethod: cptManuallySet ? 'manual' : (cptDetection?.detection_method || undefined),
      cptLaterality: (selectedCPTExam ? resolveLaterality(selectedCPTExam) : null) ?? cptDetection?.laterality ?? undefined,
      cptContrast: selectedCPTExam?.contrast ?? cptDetection?.contrast ?? undefined,
      customTemplate: useCustomTemplate ? customTemplateText || undefined : undefined,
    }));

    // Sync backend auto-detected CPT into frontend state so the UI chip renders
    if (generateReport.fulfilled.match(result)) {
      const report = result.payload;
      if (report?.cpt_code && !selectedCPTExam) {
        dispatch(selectExam({
          id: 0,
          cpt_code: report.cpt_code,
          exam_name: report.cpt_exam_name || report.cpt_code,
          modality: '',
          body_region: '',
          subspecialty: null,
          laterality: cptDetection?.laterality || null,
          contrast: cptDetection?.contrast || null,
          description: null,
          report_template: null,
          is_active: true,
        }));
        console.log('CPT_SYNC_FROM_REPORT | cpt_code=%s | laterality=%s | contrast=%s | session_id=%s', report.cpt_code, cptDetection?.laterality, cptDetection?.contrast, effectiveSessionId);
      }

      // Auto-finalize if hard lock is disabled
      const hardLockEnabled = reportConfig?.hardLockEnabled ?? false;
      if (!hardLockEnabled && report?.id) {
        await dispatch(finalizeReport(report.id));
      }
    }

    // Sync saved state so the UI is clean (no dirty indicator) after generation.
    // reportVersion was just incremented above; match savedReportVersion to it.
    setSavedReportVersion(reportVersion + 1);
    setShowDictationCompleteSnack(false);
  };

  const handleRegenerateReport = async () => {
    if (isReadOnly) {
      console.warn('REPORT_EDIT_BLOCKED_FRONTEND | Regenerate blocked - report is finalized');
      return;
    }
    if (validationBlocked) {
      console.warn('REGENERATE_BLOCKED_VALIDATION | session_id=%s', sessionId);
      return;
    }
    if (sessionId) {
      // Auto-save transcription before regenerating
      const textToSave = correctedText || text;
      if (textToSave) {
        try {
          await dispatch(saveTranscription({
            text,
            sessionId,
            cptCode: selectedCPTExam?.cpt_code,
            cptExamName: selectedCPTExam?.exam_name,
            cptLaterality: selectedCPTExam?.laterality,
            cptContrast: selectedCPTExam?.contrast,
          })).unwrap();
          setLastSavedText(textToSave);
          setLastSavedPatientContext(JSON.stringify(patientContext));
          setLastSavedCPTCode(selectedCPTExam?.cpt_code ?? null);
          setSavedReportVersion(reportVersion);
        } catch (saveErr) {
          console.error('REGENERATE_SAVE_FAILED | session_id=%s error=%s', sessionId, saveErr);
        }
      }

      const hasPatientCtx = patientContext.mrn && patientContext.dob;
      const result = await dispatch(regenerateReport({
        sessionId,
        patientContext: hasPatientCtx ? patientContext : undefined,
        cptCode: selectedCPTExam?.cpt_code,
        cptExamName: selectedCPTExam?.exam_name,
        cptDetectionMethod: cptManuallySet ? 'manual' : (cptDetection?.detection_method || undefined),
        cptLaterality: (selectedCPTExam ? resolveLaterality(selectedCPTExam) : null) ?? cptDetection?.laterality ?? undefined,
        cptContrast: selectedCPTExam?.contrast ?? cptDetection?.contrast ?? undefined,
        customTemplate: useCustomTemplate ? customTemplateText || undefined : undefined,
      }));

      // Sync backend auto-detected CPT into frontend state
      if (regenerateReport.fulfilled.match(result)) {
        const report = result.payload;
        if (report?.cpt_code && !selectedCPTExam) {
          dispatch(selectExam({
            id: 0,
            cpt_code: report.cpt_code,
            exam_name: report.cpt_exam_name || report.cpt_code,
            modality: '',
            body_region: '',
            subspecialty: null,
            laterality: cptDetection?.laterality || null,
            contrast: cptDetection?.contrast || null,
            description: null,
            report_template: null,
            is_active: true,
          }));
        }

        // Auto-finalize if hard lock is disabled (consistent with generate behavior)
        const hardLockEnabled = reportConfig?.hardLockEnabled ?? false;
        if (!hardLockEnabled && report?.id) {
          await dispatch(finalizeReport(report.id));
        }
      }

      setReportVersion((v) => v + 1);
      // Sync saved state so the UI is clean (no dirty indicator) after regeneration.
      setSavedReportVersion(reportVersion + 1);
      setExamSavedAt(Date.now());
    }
  };

  const handleContinueRecording = () => {
    setAutoStartRecording(true);
    setIsContinueMode(true);
    // Do NOT dispatch enterContinueMode here — handleTranscriptionStart already
    // dispatches it (via the setIsContinueMode functional-update guard) once the
    // AudioRecorder fires onTranscriptionStart, which is the safe moment to clear
    // isAppendMode+isFinal atomically. Dispatching it early here races the first
    // addCorrectedTranscript that may arrive before the recorder mounts.
  };

  const user = useAppSelector((state) => state.auth.user);
  const radiologistName = user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username : undefined;

  // Fetch patient context for exports when session loads
  useEffect(() => {
    if (!sessionId) { setExportPatientContext(undefined); return; }
    let active = true;
    (async () => {
      try {
        const p = await patientService.getSessionPatient(sessionId);
        if (active && p) {
          setExportPatientContext({
            first_name: p.first_name ?? undefined,
            last_name: p.last_name ?? undefined,
            mrn: p.mrn ?? undefined,
            dob: p.dob ?? undefined,
            gender: p.gender ?? undefined,
            accession_number: p.accession_number ?? undefined,
            modality: p.modality ?? undefined,
            study_date: p.study_date ?? undefined,
            body_part: p.body_part ?? undefined,
          });
        }
      } catch { /* no patient linked */ }
    })();
    return () => { active = false; };
  }, [sessionId]);

  const handleExportPDF = async () => {
    if (!report || report.status !== 'final') return;
    setExporting(true);
    try {
      await exportReportPDF(report, { radiologistName, patientContext: exportPatientContext, exportTheme: reportConfig?.exportTheme });
    } catch (err: unknown) {
      setExportError((err as Error)?.message || 'PDF export failed');
    } finally {
      setExporting(false);
    }
  };

  const handlePrintReport = () => {
    if (!report || report.status !== 'final') return;
    try {
      printReport(report, { radiologistName, patientContext: exportPatientContext, exportTheme: reportConfig?.exportTheme });
    } catch (err: unknown) {
      setExportError((err as Error)?.message || 'Print failed');
    }
  };

  const handleDownloadText = () => {
    if (!report || report.status !== 'final') return;
    try {
      downloadReportText(report);
    } catch (err: unknown) {
      setExportError((err as Error)?.message || 'Download failed');
    }
  };

  const handleExportHTML = async () => {
    if (!report || report.status !== 'final') return;
    setExporting(true);
    try {
      await reportService.exportReportHTML(report.id, report.session_id);
    } catch (err: unknown) {
      setExportError((err as Error)?.message || 'HTML export failed');
    } finally {
      setExporting(false);
    }
  };

  const handleExportDOCX = async () => {
    if (!report || report.status !== 'final') return;
    setExporting(true);
    try {
      await reportService.exportReportDOCX(report.id, report.session_id);
    } catch (err: unknown) {
      setExportError((err as Error)?.message || 'Word export failed');
    } finally {
      setExporting(false);
    }
  };

  const handlePatientSelected = (patient: PatientResponse | null) => {
    if (!patient) {
      // Clear ONLY patient-identity fields; preserve CPT/study fields owned by the exam panel
      setPatientContext((prev) => ({
        ...prev,
        first_name: undefined,
        last_name: undefined,
        mrn: undefined,
        dob: undefined,
        gender: undefined,
      }));
      setSelectedPatient(null);
      dispatch(clearValidation());
      return;
    }
    // Store selected patient for history display
    setSelectedPatient(patient);
    // Merge identity fields from patient; only overwrite a study/exam field if the patient
    // record has a non-empty value for it (never wipe CPT-filled values with blanks).
    setPatientContext((prev) => ({
      ...prev,
      first_name: patient.first_name || '',
      last_name: patient.last_name || '',
      mrn: patient.mrn || '',
      dob: patient.dob || '',
      gender: patient.gender || '',
      accession_number: patient.accession_number || prev.accession_number,
      modality: patient.modality || prev.modality,
      study_date: patient.study_date || prev.study_date,
      body_part: patient.body_part || prev.body_part,
      study_description: patient.study_description || prev.study_description,
      clinical_indication: patient.clinical_indication || prev.clinical_indication,
      study_priority: patient.study_priority || prev.study_priority,
      laterality: patient.laterality || prev.laterality,
      ordering_physician_name: patient.ordering_physician_name || prev.ordering_physician_name,
    }));
    // Collapse patient accordion and open exam accordion to guide to next step
    setPatientAccordionOpen(false);
    setExamAccordionOpen(true);
    console.log(
      'PATIENT_CONTEXT_POPULATED | patient_id=%s | mrn=%s | session_id=%s',
      patient.id, patient.mrn, sessionId,
    );
  };

  const isEditingReport = editedReportText !== null;

  const handleSaveReportText = useCallback(() => {
    if (!report || editedReportText === null) return;
    // Parse the edited text into sections to send to the API
    const lines = editedReportText.split('\n');
    const sections: Record<string, string> = {};
    let currentTitle = '';
    let currentContent: string[] = [];
    for (const line of lines) {
      const headerMatch = line.match(/^([A-Z][A-Z\s/&()-]+):(.*)$/);
      if (headerMatch) {
        if (currentTitle) {
          sections[currentTitle] = currentContent.join('\n').trim();
        }
        currentTitle = headerMatch[1].trim();
        currentContent = headerMatch[2] ? [headerMatch[2].trimStart()] : [];
      } else {
        currentContent.push(line);
      }
    }
    if (currentTitle) {
      sections[currentTitle] = currentContent.join('\n').trim();
    }
    dispatch(updateReport({ reportId: report.id, data: { sections } })).then((result) => {
      if (result.meta.requestStatus === 'fulfilled') {
        setEditedReportText(null);
        setReportSaveSuccess(true);
      }
    });
  }, [report, editedReportText, dispatch]);

  // Item 8: Auto-save corrected transcript after 2.5s debounce when editing.
  // Use ref to track latest values and avoid stale closure issues in timer.
  const latestTextRef = useRef({ editedCorrectedText, correctedText, text, sessionId, cptManuallySet, selectedCPTExam });
  latestTextRef.current = { editedCorrectedText, correctedText, text, sessionId, cptManuallySet, selectedCPTExam };

  useEffect(() => {
    // Only auto-save when actively editing. Note: editedCorrectedText may be empty
    // if the user intentionally cleared the transcript — that is a valid save target.
    console.log('AUTOSAVE_EFFECT | isEditingCorrected=%s | edited_len=%d | corrected_len=%d | text_len=%d', isEditingCorrected, editedCorrectedText?.length ?? -1, correctedText?.length ?? -1, text?.length ?? -1);
    if (!isEditingCorrected) {
      console.log('AUTOSAVE_SKIP | not in edit mode');
      return;
    }

    // Check if content actually changed from what is already committed.
    if (editedCorrectedText === (correctedText || text)) {
      console.log('AUTOSAVE_SKIP | no changes from committed text');
      return;
    }
    console.log('AUTOSAVE_TIMER_START | starting 2.5s debounce');

    const timer = setTimeout(async () => {
      // Re-check with latest values at fire time to avoid race conditions.
      const { editedCorrectedText: latestEdited, correctedText: latestCorrected, text: latestRaw, sessionId: latestSessionId, cptManuallySet: latestCptManuallySet, selectedCPTExam: latestCPTExam } = latestTextRef.current;
      if (latestEdited === (latestCorrected || latestRaw)) return;

      // Persist corrected text to backend.
      // If a session already exists, use the lightweight PATCH endpoint.
      // If no session yet (fresh page, no prior Save), create one via saveTranscription.
      try {
        if (latestSessionId) {
          await transcriptionService.updateSessionTranscript(latestSessionId, { corrected_text: latestEdited ?? '' });
        } else {
          await dispatch(saveTranscription({
            text: latestEdited ?? '',
            sessionId: crypto.randomUUID(),
            correctedText: latestEdited ?? '',
            cptCode: latestCPTExam?.cpt_code,
            cptExamName: latestCPTExam?.exam_name,
            cptLaterality: latestCPTExam?.laterality,
            cptContrast: latestCPTExam?.contrast,
          })).unwrap();
        }
      } catch (err) {
        console.error('Auto-save of corrected transcript failed:', err);
        setAutoSaveError('Auto-save failed — your changes were not saved. Please try again.');
        return; // Don't commit Redux state if backend save failed
      }
      // Re-trigger CPT detection BEFORE committing text. The auto-detect effect
      // fires on correctedText change — if we clear CPT state first, the effect
      // sees selectedExam=null and cptDetection=null, allowing re-detection.
      if (!latestCptManuallySet && latestEdited && latestEdited.length > 20) {
        console.log('AUTOSAVE_CPT_CLEAR | sessionId=%s | edited_len=%d', latestSessionId, latestEdited.length);
        dispatch(selectExam(null));
        dispatch(clearDetection());
      }
      // Commit text to Redux without exiting edit mode — keeps the textarea
      // mounted so the user can keep typing without losing cursor position.
      console.log('AUTOSAVE_COMMIT | edited_len=%d', latestEdited?.length ?? 0);
      dispatch(commitEditedCorrectedText());
    }, 2500); // 2.5s debounce

    return () => clearTimeout(timer);
    // correctedText and text intentionally excluded - we use latestTextRef to avoid stale closures
    // sessionId intentionally excluded - session ID doesn't change during editing
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditingCorrected, editedCorrectedText, cptManuallySet, dispatch]);

  // Auto-save manual typed text after 2.5s debounce.
  const latestManualTextRef = useRef({ manualTypedText, sessionId, cptManuallySet, selectedCPTExam });
  latestManualTextRef.current = { manualTypedText, sessionId, cptManuallySet, selectedCPTExam };

  useEffect(() => {
    if (!isManualTypingMode) return;
    if (!manualTypedText) return;

    const timer = setTimeout(async () => {
      const { manualTypedText: latestManual, sessionId: latestSessionId, cptManuallySet: latestCptManuallySetManual, selectedCPTExam: latestCPTExamManual } = latestManualTextRef.current;
      if (!latestManual) return;

      try {
        if (latestSessionId) {
          await transcriptionService.updateSessionTranscript(latestSessionId, { corrected_text: latestManual });
        } else {
          await dispatch(saveTranscription({
            text: latestManual,
            sessionId: crypto.randomUUID(),
            correctedText: latestManual,
            cptCode: latestCPTExamManual?.cpt_code,
            cptExamName: latestCPTExamManual?.exam_name,
            cptLaterality: latestCPTExamManual?.laterality,
            cptContrast: latestCPTExamManual?.contrast,
          })).unwrap();
        }
        // Re-trigger CPT detection when transcript changes and CPT was not manually chosen
        if (!latestCptManuallySetManual && latestManual.length > 20) {
          dispatch(selectExam(null));
          dispatch(clearDetection());
        }
      } catch (err) {
        console.error('Auto-save of manual typed text failed:', err);
        setAutoSaveError('Auto-save failed — your changes were not saved. Please try again.');
      }
    }, 2500);

    return () => clearTimeout(timer);
  }, [isManualTypingMode, manualTypedText, cptManuallySet, dispatch]);

  // Fix #10: Reset isStopping when this component unmounts mid-stop so the flag
  // cannot stay stuck true if the user navigates away during finalization.
  useEffect(() => {
    return () => {
      setIsStopping(false);
    };
  }, []);

  // ── Keyboard shortcuts ─────────────────────────────────────────────
  // Use a ref to hold latest values so the handler doesn't go stale.
  const kbStateRef = useRef({
    isTranscribing: false,
    isStopping: false,
    isReadOnly: false,
    isEditingCorrected: false,
    isFinal: false,
    isContinueMode: false,
    hardLockEnabled: false,
    report: null as typeof report,
    isFinalized: false,
    reportLoading: false,
    reportSaving: false,
    sessionId: null as string | null,
    generateBtnState: 'not_ready' as typeof generateBtnState,
  });
  kbStateRef.current = {
    isTranscribing, isStopping, isReadOnly, isEditingCorrected,
    isFinal, isContinueMode, hardLockEnabled,
    report, isFinalized, reportLoading, reportSaving, sessionId,
    generateBtnState,
  };

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      const isTyping = tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement)?.isContentEditable;
      const isMod = e.ctrlKey || e.metaKey;
      const s = kbStateRef.current;

      // Space — Start / Stop recording (only when not focused in a text field)
      if (e.code === 'Space' && !isTyping && !isMod && !e.shiftKey) {
        e.preventDefault();
        if (s.isTranscribing) {
          setStopRecordingTrigger((prev) => prev + 1);
        } else if (!s.isReadOnly && !s.isStopping) {
          const canContinue = s.isFinal && !s.isEditingCorrected && s.sessionId && (!s.isFinalized || !s.hardLockEnabled);
          if (canContinue && !s.isContinueMode) {
            handleContinueRecording();
          } else {
            setAutoStartRecording(true);
          }
        }
        return;
      }

      if (!isMod) return;

      // Ctrl/Cmd+G — Generate report
      if (e.key === 'g' && !e.shiftKey) {
        e.preventDefault();
        if (s.generateBtnState === 'ready') {
          handleOpenGenerateDialog();
        }
        return;
      }

      // Ctrl/Cmd+Shift+F — Finalize report
      if (e.key === 'F' && e.shiftKey) {
        e.preventDefault();
        if (s.report && !s.isFinalized && !s.reportLoading && !s.reportSaving) {
          setShowFinalizeConfirm(true);
        }
        return;
      }

      // Ctrl/Cmd+Shift+C — Copy report text
      if (e.key === 'C' && e.shiftKey) {
        e.preventDefault();
        if (s.report?.report_text) {
          navigator.clipboard.writeText(s.report.report_text).catch(() => {
            const ta = document.createElement('textarea');
            ta.value = s.report?.report_text ?? '';
            document.body.appendChild(ta);
            ta.select();
            document.execCommand('copy');
            document.body.removeChild(ta);
          });
        }
        return;
      }

      // Ctrl/Cmd+E — Open report editor
      if (e.key === 'e' && !e.shiftKey) {
        e.preventDefault();
        if (s.report && s.sessionId) {
          const mode = s.isReadOnly ? 'view' : 'edit';
          navigate(`/report/edit?session=${s.sessionId}&mode=${mode}`);
        }
        return;
      }
    };

    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canRegenerateReport =
    isFinal && !isTranscribing && !isEditingCorrected && sessionId && report && !reportLoading && !isReadOnly;

  const canFinalizeReport =
    report && !isFinalized && !reportLoading && !reportSaving && !isEditingReport;

  // Allow continue recording if:
  // - Session is final and not currently transcribing
  // - Hard lock is disabled (even if report is finalized, we can add dictation)
  const canContinueRecording =
    isFinal && !isTranscribing && !isEditingCorrected && sessionId && (!isFinalized || !hardLockEnabled);

  // Save is enabled when transcript, patient context, CPT exam, or report has unsaved changes
  const currentText = correctedText || text;
  const patientContextChanged = JSON.stringify(patientContext) !== lastSavedPatientContext;
  const cptChanged = (selectedCPTExam?.cpt_code ?? null) !== (lastSavedCPTCode ?? null);
  const reportChanged = reportVersion !== savedReportVersion;
  const hasUnsavedChanges = useMemo(() =>
    (!!currentText && currentText !== lastSavedText)
      || patientContextChanged
      || cptChanged
      || reportChanged,
    [currentText, lastSavedText, patientContextChanged, cptChanged, reportChanged]
  );
  const isSaveDisabled = loading || !hasUnsavedChanges;

  // Memoize transcript unsaved state for TranscriptPanel to avoid new reference each render
  // Only flag unsaved when the edited content actually differs from the saved text.
  // isEditingCorrected is intentionally excluded: the indicator should show 'Saved'
  // after autosave commits, not stay 'Unsaved' indefinitely while the user is in edit mode.
  const transcriptUnsavedChanges = useMemo(() =>
    !!editedCorrectedText && editedCorrectedText !== (correctedText || text),
    [editedCorrectedText, correctedText, text]
  );

  const handleFinalizeReport = () => {
    if (!report || isFinalized) return;
    dispatch(finalizeReport(report.id));
    setShowFinalizeConfirm(false);
  };

  const handleNewDictation = () => {
    dispatch(resetTranscription());
    dispatch(clearReport());
    dispatch(resetCPTState());
    dispatch(clearValidation());
    setPatientContext({});
    setSelectedPatient(null);
    setIsContinueMode(false);
    setEditedReportText(null);
    setLastSavedText(null);
    setSessionHasAudio(false);
    navigate('/transcribe');
  }

  const handleCloseAlert = () => setShowSuccessAlert(false);
  const handleCloseError = () => dispatch(clearError());

  // Generate button tooltip when not ready
  const generateTooltip = generateBtnState === 'not_ready'
    ? readiness.blockReasons.join('\n') || 'Pipeline not ready'
    : '';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 1 }}>
      {/* Page header — back + Save + New Dictation */}
      <PageHeaderNavigation
        title="Dictation"
        onBack={() => canGoBackInApp ? navigate(-1) : navigate('/')}
        backLabel="Back"
        actions={
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {/* CPT exam combo — always visible, left of Save / New Dictation */}
            <CPTChipCombo
              disabled={isTranscribing || isReadOnly}
              onSelect={(exam) => {
                if (exam) {
                  const sid = sessionId ?? searchParams.get('session');
                  if (sid) sessionStorage.removeItem(`cpt_cleared_${sid}`);
                }
              }}
              onClear={() => {
                const sid = sessionId ?? searchParams.get('session');
                if (sid) sessionStorage.setItem(`cpt_cleared_${sid}`, 'true');
              }}
            />
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />

            {isFinal && (correctedText || text) && !isTranscribing && !isEditingCorrected && !isReadOnly && (
              <Tooltip title={isSaveDisabled && lastSavedText ? 'Already saved — no changes' : ''} arrow>
                <span>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                    onClick={handleSaveTranscription}
                    disabled={isSaveDisabled}
                  >
                    Save
                  </Button>
                </span>
              </Tooltip>
            )}
            <Button
                variant="outlined"
                size="small"
                startIcon={<NoteAddIcon />}
                onClick={handleNewDictation}
                disabled={isTranscribing || loading}
                sx={{ borderColor: 'divider', color: 'text.secondary' }}
              >
                New Dictation
              </Button>
            
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />

            {/* Start / Add Dictation — mic icon in header toolbar */}
            {!isReadOnly && !isTranscribing && (
              <Tooltip
                title={
                  canContinueRecording
                    ? (isFinalized ? 'Add Dictation' : 'Continue Recording')
                    : 'Start Dictation'
                }
                arrow
              >
                <IconButton
                  size="small"
                  color="primary"
                  aria-label={canContinueRecording && !isContinueMode ? 'Continue recording' : 'Start recording'}
                  disabled={loading || autoStartRecording}
                  onClick={() => {
                    // Start recording without opening the left panel
                    if (canContinueRecording && !isContinueMode) {
                      handleContinueRecording();
                    } else {
                      // New session — set autoStart so AudioRecorder begins immediately
                      setAutoStartRecording(true);
                    }
                  }}
                  sx={{ ml: 0.5 }}
                >
                  <MicIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {isTranscribing && (
              <>
                <Chip
                  icon={<MicIcon sx={{ fontSize: 14 }} />}
                  label={isStopping ? 'Stopping...' : 'Recording'}
                  size="small"
                  color={isStopping ? 'warning' : 'error'}
                  sx={{ ml: 0.5, fontWeight: 600, fontSize: '0.7rem', animation: isStopping ? undefined : 'pulse 1.5s infinite', '@keyframes pulse': { '0%': { opacity: 1 }, '50%': { opacity: 0.6 }, '100%': { opacity: 1 } } }}
                />
                <Tooltip title={isStopping ? 'Stopping...' : 'Stop recording'} arrow>
                  <span>
                    <IconButton
                      size="small"
                      color="error"
                      aria-label={isStopping ? 'Stopping recording' : 'Stop recording'}
                      onClick={() => setStopRecordingTrigger(prev => prev + 1)}
                      disabled={isStopping}
                      sx={{ ml: 0.5 }}
                    >
                      <StopIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </>
            )}

            {showToolbarToggles && (
              <>
                <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />

                {/* Context panels — topmost panel in layout, so first in toggle order */}
                <Tooltip title={showContextPanels ? "Hide patient & exam context" : "Show patient & exam context"} arrow>
                  <IconButton
                    size="small"
                    aria-label={showContextPanels ? 'Hide patient & exam context' : 'Show patient & exam context'}
                    onClick={() => dispatch(toggleContextPanels())}
                    color={showContextPanels ? 'primary' : 'default'}
                    sx={{ ml: 0.5 }}
                  >
                    <AssignmentIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                {/* Dictation capture — middle panel (hidden in deferred correction mode) */}
                {!deferredCorrectionEnabled && (
                  <Tooltip title={showDictationCapture ? "Hide dictation capture" : "Show dictation capture"} arrow>
                    <IconButton
                      size="small"
                      aria-label={showDictationCapture ? 'Hide dictation capture' : 'Show dictation capture'}
                      onClick={() => setShowDictationCapture(!showDictationCapture)}
                      color={showDictationCapture ? 'primary' : 'default'}
                      sx={{ ml: 0.5 }}
                    >
                      <TextSnippetIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}

                {/* Audio playback — below dictation */}
                <Tooltip title={isTranscribing ? "Cannot switch view while recording" : (showAudioPlayback ? "Hide audio playback" : "Show audio playback")} arrow>
                  <span>
                    <IconButton
                      size="small"
                      aria-label={showAudioPlayback ? 'Hide audio playback' : 'Show audio playback'}
                      onClick={() => !isTranscribing && setShowAudioPlayback(!showAudioPlayback)}
                      color={showAudioPlayback ? 'primary' : 'default'}
                      disabled={isTranscribing}
                    >
                      <HeadphonesIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>

                {/* Workflow stepper — rendered at bottom of page, so last in toggle order */}
                <Tooltip title={showWorkflowStepper ? 'Hide workflow progress' : 'Show workflow progress'} arrow>
                  <IconButton
                    size="small"
                    aria-label={showWorkflowStepper ? 'Hide workflow progress' : 'Show workflow progress'}
                    onClick={() => setShowWorkflowStepper(!showWorkflowStepper)}
                    color={showWorkflowStepper ? 'primary' : 'default'}
                    sx={{ ml: 0.5 }}
                  >
                    <TimelineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />
              </>
            )}

            {/* Addendum mode indicator — compact chip with tooltip instead of full-width banner */}
            {isFinalized && isFinal && !isReadOnly && !isTranscribing && (
              <Tooltip
                title="This case is finalized. You can add additional dictation or manually type findings, then regenerate the report. The new findings will be incorporated into an addendum."
                arrow
              >
                <Chip
                  icon={<NoteAddIcon sx={{ fontSize: 14 }} />}
                  label="Addendum"
                  size="small"
                  color="warning"
                  variant="outlined"
                  sx={{ ml: 1, cursor: 'default', fontWeight: 600, fontSize: '0.7rem' }}
                />
              </Tooltip>
            )}

            {/* CPT chip now rendered at the start of the actions bar (see above) */}
          </Box>
        }
      />

      {/* Main content area - grows to fill available space */}
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, gap: 1 }}>
        {/* Read-only mode alert */}
        {isReadOnly && (
          <Alert severity="info" icon={<DescriptionIcon />} sx={{ borderRadius: 2 }}>
            <strong>View-Only Mode:</strong> This report is finalized and cannot be edited or regenerated.
          </Alert>
        )}

        {/* Addendum mode indicator moved to header toolbar chip — see below */}

        {/* Hidden AudioRecorder — rendered when audio panel is hidden but recording should be active.
             Also rendered when isFinal+isContinueMode so an in-progress continue-recording session
             is not disrupted by isFinal becoming true before the new segment finishes. */}
        {!showAudioPlayback && !isReadOnly && (
          <Box sx={{ position: 'absolute', left: -9999, top: -9999, width: 0, height: 0, overflow: 'hidden' }}>
            <AudioRecorder
              onTranscriptionStart={handleTranscriptionStart}
              onTranscriptionEnd={handleTranscriptionEnd}
              onTranscriptionStopping={handleTranscriptionStopping}
              existingSessionId={isAppendMode || isContinueMode ? sessionId : undefined}
              autoStart={autoStartRecording}
              stopTrigger={stopRecordingTrigger}
            />
          </Box>
        )}

        {/* ═══════════════ TOP: COLLAPSIBLE CONTEXT PANELS ═══════════════ */}
        {/* Item 2: Only show when showContextPanels=true and not read-only */}
        {!isReadOnly && showContextPanels && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0,
              minHeight: 0,
              maxHeight: isTranscribing ? 0 : 800,
              overflow: 'hidden',
              opacity: isTranscribing ? 0 : 1,
              transition: 'max-height 0.4s ease, opacity 0.3s ease',
              mb: isTranscribing ? 0 : undefined,
            }}
          >
          {/* Accordion 1: Patient Details — search + demographics */}
          <Accordion
            expanded={patientAccordionOpen}
            onChange={() => setPatientAccordionOpen(!patientAccordionOpen)}
            disableGutters
            elevation={0}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '8px !important',
              mb: 0.75,
              '&:before': { display: 'none' },
              '& .MuiAccordionSummary-root': { minHeight: 40, py: 0 },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', flexWrap: 'wrap' }}>
                <PersonOutlineIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                <Typography variant="subtitle2" fontWeight={700} fontSize="0.8rem">
                  Patient
                </Typography>
                {/* Read-only summary when collapsed */}
                {!patientAccordionOpen && selectedPatient && (
                  <Box sx={{ display: 'flex', gap: 0.5, ml: 1, flexWrap: 'wrap' }}>
                    {patientContext.first_name && (
                      <Chip
                        label={`${patientContext.first_name} ${patientContext.last_name || ''}`.trim()}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600 }}
                      />
                    )}
                    {patientContext.mrn && (
                      <Chip label={`MRN: ${patientContext.mrn}`} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                    )}
                    {patientContext.dob && (
                      <Chip label={`DOB: ${patientContext.dob}`} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                    )}
                    {patientContext.gender && (
                      <Chip label={patientContext.gender === 'M' ? 'Male' : patientContext.gender === 'F' ? 'Female' : patientContext.gender} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                    )}
                  </Box>
                )}
                {!patientAccordionOpen && !selectedPatient && (
                  <Typography variant="caption" color="text.disabled" sx={{ ml: 1 }}>
                    No patient selected
                  </Typography>
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, pb: 1.5, px: 2 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <PatientSearchPanel
                  onPatientSelected={handlePatientSelected}
                  disabled={reportLoading || isTranscribing}
                  sessionId={sessionId}
                />
                <PatientDemographicsPanel
                  value={patientContext}
                  onChange={setPatientContext}
                  onPatientSelected={handlePatientSelected}
                  disabled={reportLoading || isTranscribing}
                  sessionId={sessionId || undefined}
                />
                {/* Patient History — collapsed sub-section */}
                {selectedPatient && (
                  <PatientHistorySummary
                    patientId={selectedPatient.id}
                    limit={5}
                  />
                )}
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Accordion 2: Current Examination — CPT exam + study fields */}
          <Accordion
            expanded={examAccordionOpen}
            onChange={() => setExamAccordionOpen(!examAccordionOpen)}
            disableGutters
            elevation={0}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '8px !important',
              mb: 0.75,
              '&:before': { display: 'none' },
              '& .MuiAccordionSummary-root': { minHeight: 40, py: 0 },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', flexWrap: 'wrap' }}>
                <AssignmentIcon sx={{ fontSize: 18, color: 'secondary.main' }} />
                <Typography variant="subtitle2" fontWeight={700} fontSize="0.8rem">
                  Current Examination
                </Typography>
                {/* Read-only summary when collapsed */}
                {!examAccordionOpen && (selectedCPTExam || patientContext.modality) && (
                  <Box sx={{ display: 'flex', gap: 0.5, ml: 1, flexWrap: 'wrap' }}>
                    {selectedCPTExam && (
                      <Chip
                        label={selectedCPTExam.cpt_code}
                        size="small"
                        color="secondary"
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600 }}
                      />
                    )}
                    {patientContext.modality && (
                      <Chip label={patientContext.modality} size="small" color="info" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                    )}
                    {patientContext.body_part && (
                      <Chip label={patientContext.body_part} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                    )}
                    {patientContext.study_date && (
                      <Chip label={patientContext.study_date} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                    )}
                    {patientContext.laterality && (
                      <Chip label={patientContext.laterality} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                    )}
                    {selectedCPTExam?.exam_name && (
                      <Chip
                        label={selectedCPTExam.exam_name.length > 50 ? selectedCPTExam.exam_name.substring(0, 50) + '...' : selectedCPTExam.exam_name}
                        size="small"
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.65rem', maxWidth: 350 }}
                      />
                    )}
                  </Box>
                )}
                {!examAccordionOpen && !selectedCPTExam && !patientContext.modality && (
                  <Typography variant="caption" color="text.disabled" sx={{ ml: 1 }}>
                    No exam configured
                  </Typography>
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, pb: 1.5, px: 2 }}>
              <CurrentExaminationPanel
                value={patientContext}
                onChange={setPatientContext}
                onSystemChange={(ctx) => {
                  // System-initiated auto-population: update both state and saved baseline
                  // so the change does not register as dirty.
                  setPatientContext(ctx);
                  setLastSavedPatientContext(JSON.stringify(ctx));
                }}
                onCPTCleared={() => {
                  // Persist the user's explicit clear so page reloads don't re-trigger auto-detect
                  const sid = sessionId ?? searchParams.get('session');
                  if (sid) {
                    sessionStorage.setItem(`cpt_cleared_${sid}`, 'true');
                    console.log('CPT_USER_CLEARED | session_id=%s', sid);
                  }
                }}
                savedAt={examSavedAt}
                disabled={reportLoading || isTranscribing}
                isTranscribing={isTranscribing}
                isReadOnly={isReadOnly}
                correctedText={correctedText}
                rawText={text}
                isFinal={isFinal}
                report={report}
                sessionId={sessionId ?? searchParams.get('session') ?? undefined}
              />
            </AccordionDetails>
          </Accordion>
        </Box>
      )}

      {(validationIssues.length > 0 || validationLoading) && (
        <Box sx={{ mb: 1.25 }}>
          <ExamPatientValidationAlert
            issues={validationIssues}
            loading={validationLoading}
            blocked={validationBlocked}
          />
        </Box>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: showAudioPlayback ? '260px 1fr' : '1fr',
          gridTemplateRows: '1fr',
          gap: showAudioPlayback ? 1.5 : 0,
          flex: 1,
          minHeight: 0,
        }}
      >
        {/* ═══════════════ LEFT PANEL ═══════════════ */}
        {/* Show/Hide Audio Playback toggle controls left panel visibility */}
        {showAudioPlayback && (
          <Paper
            elevation={1}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              p: 2,
              overflow: 'auto',
              borderRadius: 2,
              '&::-webkit-scrollbar': { width: 4 },
              '&::-webkit-scrollbar-thumb': { bgcolor: 'divider', borderRadius: 2 },
            }}
          >
            {/* Audio Playback */}
            <AudioPanel
              sessionId={sessionId}
              isTranscribing={isTranscribing}
              isFinal={isFinal}
              hasAudio={sessionHasAudio}
            />

            <Divider sx={{ my: 1.5 }} />

            {/* Session Controls */}
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={600}
              textTransform="uppercase"
              letterSpacing={0.5}
              sx={{ mb: 1 }}
            >
              Dictation Controls
            </Typography>

            {/* Mode indicator */}
            {isAppendMode && !isTranscribing && (
              <Chip
                label="Loaded Session"
                size="small"
                color="info"
                variant="outlined"
                sx={{ mb: 1, alignSelf: 'flex-start' }}
              />
            )}

            {(isTranscribing || (!isFinal && (!isAppendMode || isTranscribing)) || (isContinueMode && !isTranscribing)) ? (
              <AudioRecorder
                onTranscriptionStart={handleTranscriptionStart}
                onTranscriptionEnd={handleTranscriptionEnd}
                onTranscriptionStopping={handleTranscriptionStopping}
                existingSessionId={isAppendMode || isContinueMode ? sessionId : undefined}
                autoStart={autoStartRecording}
                stopTrigger={stopRecordingTrigger}
              />
            ) : null}

            {/* Continue Recording / Add Dictation button — shown when viewing a loaded session */}
            {canContinueRecording && !isContinueMode && (
              <Button
                variant="outlined"
                color={isFinalized ? 'secondary' : 'primary'}
                startIcon={<MicIcon />}
                onClick={handleContinueRecording}
                fullWidth
                size="small"
                sx={{ mt: 1 }}
              >
                {isFinalized ? 'Add Dictation' : 'Continue Recording'}
              </Button>
            )}

            {/* Status / Pipeline */}
            <Divider sx={{ my: 1.5 }} />
            <StatusPanel
              isTranscribing={isTranscribing}
              isFinal={isFinal}
              isFinalized={isFinalized}
              isIncrementalCorrecting={isIncrementalCorrecting}
              isRefiningInFlight={isRefiningInFlight}
            />

            {/* Spacer */}
            <Box sx={{ flex: 1 }} />
          </Paper>
        )}

      {/* ═══════════════ MAIN CONTENT AREA ═══════════════ */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: reportLayout === 'bottom' ? 'column' : 'row',
          gap: 1.5,
          minHeight: 0,
          flex: 1,
        }}
      >
        {/* ── Dictation Section (50%) ── */}
        <Paper
          elevation={1}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            p: 2,
            overflow: 'hidden',
            minHeight: 0,
            borderRadius: 2,
            flex: 1,
          }}
        >
          {/* Dictation header with Generate/Regen button + layout toggle */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: 0.5, gap: 1 }}>
            {/* Custom Template Toggle — always visible */}
            <Tooltip title="Toggle between auto-selected CPT template and a custom template structure">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mr: 1, cursor: 'help' }}>
                <Typography variant="caption" color={useCustomTemplate ? 'text.secondary' : 'primary'} fontWeight={500}>
                  Auto
                </Typography>
                <Switch
                  checked={useCustomTemplate}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setUseCustomTemplate(e.target.checked);
                  }}
                  disabled={!isFinal || !(correctedText || text) || isTranscribing || isEditingCorrected || isReadOnly}
                  size="small"
                />
                <Typography variant="caption" color={useCustomTemplate ? 'primary' : 'text.secondary'} fontWeight={500}>
                  Use custom template
                </Typography>
              </Box>
            </Tooltip>
            {/* Generate / Regenerate Report — always visible, disabled when not ready */}
            {canRegenerateReport ? (
              <Button
                variant="outlined"
                color="primary"
                startIcon={<RefreshIcon />}
                onClick={handleRegenerateReport}
                disabled={reportLoading || !isFinal || !(correctedText || text) || isTranscribing || isEditingCorrected || isReadOnly || (useCustomTemplate && !customTemplateText.trim())}
                size="small"
              >
                {reportLoading ? 'Regenerating...' : 'Regenerate Report'}
              </Button>
            ) : (
              <Tooltip title={useCustomTemplate && !customTemplateText.trim() ? 'Paste a custom template to enable report generation' : generateTooltip} arrow placement="top">
                <span>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={
                      generateBtnState === 'generating'
                        ? <CircularProgress size={14} color="inherit" />
                        : <DescriptionIcon />
                    }
                    onClick={handleOpenGenerateDialog}
                    disabled={generateBtnState === 'not_ready' || generateBtnState === 'generating' || (useCustomTemplate && !customTemplateText.trim())}
                    size="small"
                    sx={generateBtnState === 'ready' && !(useCustomTemplate && !customTemplateText.trim()) ? {
                      animation: 'generatePulse 2s ease-in-out infinite',
                      '@keyframes generatePulse': {
                        '0%, 100%': { boxShadow: '0 0 0 0 rgba(25,118,210,0.4)' },
                        '50%': { boxShadow: '0 0 0 6px rgba(25,118,210,0)' },
                      },
                    } : undefined}
                  >
                    {generateBtnState === 'generating' ? 'Creating...' : 'Generate Report'}
                  </Button>
                </span>
              </Tooltip>
            )}
            {/* Open Report Editor — aligned with Regenerate */}
            {report && (
              <Button
                variant="outlined"
                color="primary"
                startIcon={isReadOnly ? <LockIcon /> : <OpenInNewIcon />}
                onClick={() => {
                  const mode = isReadOnly ? 'view' : 'edit';
                  navigate(`/report/edit?session=${sessionId}&mode=${mode}`);
                }}
                size="small"
              >
                {isReadOnly ? 'View Report' : 'Open Report Editor'}
              </Button>
            )}
            {/* Layout toggle icon */}
            <Tooltip title={reportLayout === 'bottom' ? 'Show report on right' : 'Show report on bottom'} arrow>
              <IconButton
                size="small"
                aria-label={reportLayout === 'bottom' ? 'Show report on right' : 'Show report on bottom'}
                onClick={() => setReportLayout(reportLayout === 'bottom' ? 'right' : 'bottom')}
                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 0.5 }}
              >
                {reportLayout === 'bottom' ? <ViewColumnIcon sx={{ fontSize: 18 }} /> : <ViewStreamIcon sx={{ fontSize: 18 }} />}
              </IconButton>
            </Tooltip>
          </Box>

          {/* Custom Template Input Panel — shown when Custom mode is enabled */}
          {useCustomTemplate && (
            <Box sx={{ mb: 1.5, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper', opacity: (!isFinal || !(correctedText || text) || isTranscribing || isEditingCorrected || isReadOnly) ? 0.5 : 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography variant="subtitle2" color="primary" fontSize="0.8rem">
                  Custom Template
                </Typography>
                <Tooltip title="Paste a custom report template to guide the LLM output format">
                  <DescriptionIcon fontSize="small" color="action" />
                </Tooltip>
              </Box>
              <TextField
                fullWidth
                multiline
                rows={4}
                placeholder={`Paste your custom report template structure here...

Example:
EXAMINATION: CT of the ...
Clinical Data: ...
TECHNIQUE: ...
FINDINGS: ...
IMPRESSION:
1. ...
2. ...
3. ...`}
                value={customTemplateText}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCustomTemplateText(e.target.value)}
                disabled={isTranscribing || reportLoading || !isFinal || !(correctedText || text) || isEditingCorrected || isReadOnly}
                size="small"
                sx={{
                  '& .MuiInputBase-root': {
                    fontFamily: 'monospace',
                    fontSize: '0.8rem',
                  },
                }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                This template is used for this session only. The predefined prompt will guide the LLM to use this structure.
              </Typography>
            </Box>
          )}

          {/* Gap 1 & 4: Dictation Capture and Reviewed Dictation with auto-save */}
          <TranscriptPanel
            isTranscribing={isTranscribing}
            isReadOnly={isReadOnly}
            showDictationCapture={!deferredCorrectionEnabled && showDictationCapture}
            hasUnsavedChanges={transcriptUnsavedChanges}
          />
        </Paper>

        {/* ── Report Section (50%) ── */}
        <Paper
          elevation={1}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            p: 2,
            overflow: 'hidden',
            minHeight: 0,
            borderRadius: 2,
            flex: 1,
          }}
        >
          {/* Report header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={600}
              textTransform="uppercase"
              letterSpacing={0.5}
            >
              Radiology Report
            </Typography>
            {report && (
              <Chip
                label={report.status === 'final' ? (hardLockEnabled ? 'GENERATED' : 'FINALIZED') : report.status === 'review' ? 'REVIEW' : 'DRAFT'}
                size="small"
                color={report.status === 'final' ? 'success' : report.status === 'review' ? 'info' : 'warning'}
                icon={report.status === 'final' && hardLockEnabled ? <LockIcon sx={{ fontSize: 14 }} /> : undefined}
                sx={{ fontSize: '0.6rem', height: 20, fontWeight: 700 }}
              />
            )}
          </Box>

          {report ? (
            <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              {/* Report action bar: Edit / Save / Finalize */}
              <Box sx={{ display: 'flex', gap: 0.5, mb: 1, justifyContent: 'flex-end', alignItems: 'center' }}>
                <Tooltip title={copied ? 'Copied!' : 'Copy report'} arrow>
                  <IconButton
                    size="small"
                    aria-label={copied ? 'Copied!' : 'Copy report'}
                    onClick={async () => {
                      if (!report?.report_text) return;
                      try {
                        await copyReportToClipboard(report.report_text);
                      } catch {
                        // ignored — utility already has plain-text fallback
                      }
                      setCopied(true);
                      setTimeout(() => setCopied(false), 2500);
                    }}
                    color={copied ? 'success' : 'default'}
                  >
                    {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
                {!isReadOnly && (
                  isEditingReport ? (
                    <>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setEditedReportText(null)}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={reportSaving ? <CircularProgress size={12} color="inherit" /> : <CheckIcon />}
                        onClick={handleSaveReportText}
                        disabled={reportSaving || editedReportText === report.report_text}
                      >
                        {reportSaving ? 'Saving...' : 'Save Changes'}
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => setEditedReportText(report.report_text)}
                    >
                      Edit
                    </Button>
                  )
                )}
                {/* Export buttons — visible only when finalized */}
              {isFinalized && (
                <>
                  <Tooltip title="Export as PDF" arrow>
                    <IconButton size="small" aria-label="Export as PDF" onClick={handleExportPDF} disabled={exporting}>
                      <PictureAsPdfIcon fontSize="small" color="error" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Download HTML" arrow>
                    <IconButton size="small" aria-label="Download HTML" onClick={handleExportHTML} disabled={exporting}>
                      <CodeIcon fontSize="small" color="primary" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Download Word" arrow>
                    <IconButton size="small" aria-label="Download Word" onClick={handleExportDOCX} disabled={exporting}>
                      <ArticleIcon fontSize="small" color="primary" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Print report" arrow>
                    <IconButton size="small" aria-label="Print report" onClick={handlePrintReport}>
                      <PrintIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Download as text" arrow>
                    <IconButton size="small" aria-label="Download as text" onClick={handleDownloadText}>
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              )}
              {/* Finalize button — hidden when hard lock is enabled */}
              {!hardLockEnabled && (
                <Tooltip
                  title={isFinalized ? 'Report is finalized - editing allowed' : 'Lock this report as final — cannot be undone'}
                  arrow
                >
                  <span>
                    <Button
                      variant={isFinalized ? 'outlined' : 'contained'}
                      size="small"
                      color={isFinalized ? 'success' : 'success'}
                      startIcon={
                        reportSaving
                          ? <CircularProgress size={12} color="inherit" />
                          : isFinalized
                            ? <CheckCircleIcon sx={{ fontSize: 14 }} />
                            : <LockOpenIcon sx={{ fontSize: 14 }} />
                      }
                      onClick={() => !isFinalized && setShowFinalizeConfirm(true)}
                      disabled={!canFinalizeReport && !isFinalized}
                      sx={{ fontSize: '0.7rem', py: 0.25, textTransform: 'none' }}
                    >
                      {reportSaving ? 'Finalizing...' : isFinalized ? '' : 'Finalize Report'}
                    </Button>
                  </span>
                </Tooltip>
              )}
              </Box>
              <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                <RichReportEditor
                  value={isEditingReport ? (editedReportText ?? report.report_text) : report.report_text}
                  onChange={(text) => setEditedReportText(text)}
                  editing={isEditingReport}
                />
              </Box>
            </Box>
          ) : reportLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">
                Generating report...
              </Typography>
            </Box>
          ) : (
            <Box sx={{ py: 2, textAlign: 'center' }}>
              <DescriptionIcon sx={{ fontSize: 28, color: 'text.disabled', mb: 0.5 }} />
              <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
                No report yet. Generate after completing dictation.
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>
      </Box>

      {/* ═══════════════ DIALOGS & SNACKBARS ═══════════════ */}

      {/* Pre-generation confirmation dialog */}

      {/* Dictation complete auto-prompt */}
      <Snackbar
        open={showDictationCompleteSnack}
        autoHideDuration={15000}
        onClose={() => setShowDictationCompleteSnack(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowDictationCompleteSnack(false)}
          severity="info"
          sx={{ width: '100%' }}
          action={
            readiness.pipelineReady && !reportLoading ? (
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  setShowDictationCompleteSnack(false);
                  handleOpenGenerateDialog(); // triggers direct generation
                }}
                sx={{ fontWeight: 700 }}
              >
                Generate Now
              </Button>
            ) : undefined
          }
        >
          Dictation complete. {readiness.pipelineReady ? 'Ready to generate report.' : 'Waiting for pipeline...'}
        </Alert>
      </Snackbar>

      <Snackbar
        open={showSuccessAlert}
        autoHideDuration={6000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseAlert} severity="success" sx={{ width: '100%' }}>
          Dictation saved successfully!
        </Alert>
      </Snackbar>

      <Snackbar
        open={reportSaveSuccess}
        autoHideDuration={4000}
        onClose={() => setReportSaveSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setReportSaveSuccess(false)} severity="success" sx={{ width: '100%' }}>
          Report changes saved successfully!
        </Alert>
      </Snackbar>

      {/* Finalize confirmation prompt */}
      <Snackbar
        open={showFinalizeConfirm}
        onClose={() => setShowFinalizeConfirm(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="info"
          icon={false}
          sx={{ width: '100%', alignItems: 'center', bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', color: 'text.primary' }}
          action={
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                color="inherit"
                size="small"
                onClick={() => setShowFinalizeConfirm(false)}
              >
                Cancel
              </Button>
              <Button
                color="success"
                variant="contained"
                size="small"
                startIcon={<LockIcon sx={{ fontSize: 14 }} />}
                onClick={handleFinalizeReport}
              >
                Confirm Finalize
              </Button>
            </Box>
          }
        >
          This will lock the report permanently. Are you sure?
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!exportError}
        autoHideDuration={5000}
        onClose={() => setExportError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setExportError(null)} severity="error" sx={{ width: '100%' }}>
          {exportError}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!autoSaveError}
        autoHideDuration={8000}
        onClose={() => setAutoSaveError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setAutoSaveError(null)} severity="warning" sx={{ width: '100%' }}>
          {autoSaveError}
        </Alert>
      </Snackbar>
      </Box>

      {/* Workflow Stepper — rendered at bottom of page, toggled via toolbar */}
      {showWorkflowStepper && <WorkflowStepper />}

    </Box>
  );
};

export default TranscriptionPage;